import React, { useRef} from 'react';
import Lottie from 'lottie-react';
import styled from "styled-components";
import {
    mediaLaptop,
    mediaMaxWidthMobile,
    mediaMaxHeight,
    mediaMinHeight,
    mediaMobile,
    mediaTablet,
    mediaUnderLaptop,
    mediaUnderMinHeight,
    mediaUnderTablet,
    mediaUnderTabletQuery
} from "src/styles/mediaQuery";
import lottiePhone from "src/assets/lotties/lottie_deadline_phone.json";
import lottieTimer from "src/assets/lotties/lottie_deadline_countdown.json";
import lottieFlameD1 from "src/assets/images/event_section/img_deadline_ui_large.png";
import lottieFlameD3 from "src/assets/images/event_section/img_deadline_ui_small.png";
import imgBalloonText from "src/assets/images/event_section/img_balloon_text.svg"
import {
    LineBreak,
    Section,
    TitleLine,
    SubLine,
    LinePoint,
    VideoWrapper,
    BackgroundVideo as StyledBackgroundVideo,
} from "src/styles/common/sectionStyles";
import {motion, useInView} from "framer-motion";
import {EventSectionVideoProps} from "src/pages/section/EventSection/index";

const videoVariants = {
    initial: {
        opacity: 0,
    },
    animate: {
        opacity: 1,
        transition: {
            delay: 3.5,
            ease: "easeOut",
        },
    },
};
const textContainerVariants = {
    initial: {
        opacity: 0,
        y: 45
    },
    animate: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5,
            when: "beforeChildren",
        }
    },

};

const firstTextVariants = {
    initial: {
        opacity: 1,
        y: 0
    },
    animate: {
        opacity: 0,
        y: "-100%",
        transition: {
            delay: 2.5,
            duration:.5
        }
    },

};

const secondTextVariants = {
    initial: {
        opacity: 0,
        y: 45
    },
    animate: {
        opacity: 1,
        y: 0,
        transition: {
            delay: 3,
            duration:.5
        }
    },
};

const imageContainerVariants = {
    initial: {
        opacity: 0,
        y: 45
    },
    animate: {
        opacity: 1,
        y: 0,
        transition: {
            delay: 3,
            duration:.5
        }
    },
};

function EventSectionView({tabletVideoSrc,defaultVideoSrc}:EventSectionVideoProps) {
    const backgroundRef = useRef(null);
    const isInView = useInView(backgroundRef, {
        once: true,
        margin: "-10% 0px"
    });

    return (
        <Section className="section">
            <VideoWrapper>
                <BackgroundVideo
                    autoPlay
                    muted
                    preload="none"
                    playsInline
                    aria-hidden="true"
                >
                    <source
                        src={
                            window.matchMedia(mediaUnderTabletQuery).matches
                                ? tabletVideoSrc
                                : defaultVideoSrc
                        }
                        type="video/mp4"
                    />
                </BackgroundVideo>
            </VideoWrapper>
            <BackgroundCover
                ref={backgroundRef}
                variants={videoVariants}
                initial="initial"
                animate={isInView ? "animate" : "initial"}
            />
            <Inner>
                <ContentsWrap>
                    <Contents
                        variants={textContainerVariants}
                        initial="initial"
                        whileInView="animate"
                        viewport={{
                            once: true,
                            amount: .35,
                        }}
                    >
                        <BalloonText/>
                        <TextWrapper>
                            <FirstText
                                variants={firstTextVariants}
                                initial="initial"
                                whileInView="animate"
                                viewport={{ once: true }}
                            >
                                <TitleLine><LinePoint className="main-color">나도 모르게&nbsp;<LineBreak className="pc"/>마감</LinePoint>되는
                                    이벤트</TitleLine>
                            </FirstText>
                            <SecondText
                                variants={secondTextVariants}
                                initial="initial"
                                whileInView="animate"
                                viewport={{ once: true }}
                            >
                                <TitleLine>놓치지 말고 <LinePoint className="main-color">참여하세요</LinePoint></TitleLine>
                                <SubLine>그동안 아깝게 놓쳤던 이벤트<br/><span className="bold">이벤트U</span>에서 전부 모아
                                    보여드릴게요.</SubLine>
                            </SecondText>
                        </TextWrapper>
                    </Contents>
                    <Contents>
                        <ImageContainer  variants={imageContainerVariants}
                                         initial="initial"
                                         whileInView="animate"
                                         viewport={{ once: true }}>
                            <ImageItem className="lottie lottie-phone">
                                <Lottie animationData={lottiePhone} loop autoPlay/>
                            </ImageItem>
                            <ImageItem className="lottie lottie-timer">
                                <Lottie animationData={lottieTimer} loop autoPlay/>
                            </ImageItem>
                            <ImageItem className="lottie lottie-flame-d1" />
                            <ImageItem className="lottie lottie-flame-d3"/>
                        </ImageContainer>
                    </Contents>
                </ContentsWrap>
            </Inner>
        </Section>
    );
}
export default EventSectionView;


const Inner = styled.div`
  position: relative;
  width: 100%;
  max-width: 1460px;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  z-index: 2;

    ${mediaMaxHeight} {
      transform: scale(.85);
    }
    
    ${mediaUnderLaptop} {
      transform: scale(.85);
    }

    ${mediaMinHeight} {
      transform: scale(.70);
    }

    ${mediaUnderMinHeight} {
      transform: scale(.55);
    }

    ${mediaTablet} {
      transform: scale(1) !important;
    }
`;


const ContentsWrap = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  z-index: 2;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;

  ${mediaTablet} {
    display: flex;
    flex-flow: column-reverse;
    justify-content: center;
    gap: unset;
    height: 100%;
    text-align: center;
`;

const Contents = styled(motion.div)`
  position: relative;
  display: flex;
  width: 100%;
  flex-flow: column;
  align-items: flex-start;

  &:nth-child(1) {
    ${mediaTablet} {
      align-items: center;
      z-index: 2;
    }

    ${mediaUnderTablet} {
      align-items: center;
      z-index: 2;
    }
  }

  &:nth-child(2) {
    align-items: center;
  }
`;

const BackgroundVideo = styled(StyledBackgroundVideo)`
  ${mediaMaxWidthMobile} {
    height:80%
  }
  ${mediaMobile} {
    height:100%
  }
`
const TextWrapper = styled(motion.div)`
  position: relative;
  overflow: hidden;
  margin-top: 15px;

  ${mediaTablet} {
    margin-top: 8px;
    text-align: center;
  }
`;


const FirstText = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
`;

const SecondText = styled(motion.div)`
  min-width: 585px;

  ${mediaLaptop} {
    left: 0;
    right: 0;
    margin: 0 auto;
  }

  ${mediaUnderLaptop} {
    min-width: unset;
  }
`;
const BalloonText = styled.div`
  position: relative;
  display: inline-flex;
  z-index: 1;
  width: 163px;
  height: 73px;
  background: url(${imgBalloonText}) no-repeat center;
  background-size: contain;

  ${mediaTablet} {
    width: 110px;
    height: 49px;
  }

  ${mediaUnderTablet} {
    width: 82px;
    height: 37px;
  }
`

const ImageContainer = styled(motion.ul)`
  position: relative;
  width: 548px;
  height: 862px;
  flex-shrink: 0;

  ${mediaTablet} {
    width: 473px;
    height: 744px;
  }

  ${mediaUnderTablet} {
    width: 285px;
    height: 428px;
  }
`;


const ImageItem = styled.li`
  position: absolute;

  &.lottie-phone {
    width: 457px;
    height: 746px;
    top: 116px;
    left: 71px;

    ${mediaTablet} {
      width: 394px;
      height: 643px;
      top: 100px;
      left: 61px;
      right: unset;
      margin: unset;
    }

    ${mediaUnderTablet} {
      width: 237px;
      height: 388px;
      top: 60px;
      left: 37px;
    }
  }

  &.lottie-timer {
    width: 514px;
    height: 145px;
    top: 0;
    left: 0;
    right: 0;
    margin: 0 auto;

    ${mediaTablet} {
      width: 443px;
      height: 125px;
    }

    ${mediaUnderTablet} {
      width: 267px;
      height: 75px;
    }
  }

  &.lottie-flame-d1 {
    width: 405px;
    height: 71px;
    top: 352px;
    left: 49px;
    background:url(${lottieFlameD1}) #fff no-repeat center;
    background-size: cover;
    box-shadow: 0 25px 50px 6px rgba(0, 0, 0, 0.10);
    border-radius: 25px;
    overflow: hidden;
    ${mediaTablet} {
      width: 349px;
      height: 61px;
      border-radius: 22px;
      top: 305px;
      left: 40px;
    }

    ${mediaUnderTablet} {
      width: 211px;
      height: 37px;
      border-radius: 13px;
      top: 183px;
      left: 24px;
      box-shadow: 0 13px 26px 3px rgba(0, 0, 0, 0.10); 
    }
  }

  &.lottie-flame-d3 {
    width: 175px;
    height: 57px;
    top: 460px;
    right: 0;             
    background:url(${lottieFlameD3})#fff no-repeat center;
    background-size: cover;
    box-shadow: 0 25px 50px 6px rgba(0, 0, 0, 0.10);
    border-radius: 20px;
    overflow: hidden;
    ${mediaTablet} {
      width: 157px;
      height: 51px;
      border-radius: 18px;
      top: 395px;
    }

    ${mediaUnderTablet} {
      width: 90px;
      height: 30px;
      border-radius: 10px;
      box-shadow: 0 10px 20px 2px rgba(0, 0, 0, 0.10);
      top: 239px;
    }
  }
`;


const BackgroundCover = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 1;
`;
